<template>
  <div class="rb-ob-form rb-match-page rb-session-page">
    <header>
      <h1 class="rb-ob-title">{{ $t('intake.onlineSession.waitTitle') }}</h1>
    </header>

    <div class="rb-session-reconnect-panel">
      <div class="rb-session-wait-pulse" aria-hidden="true"></div>
      <p>{{ $t('intake.onlineSession.waitBody') }}</p>
    </div>

    <p v-if="needsLogin" class="rb-ob-status" role="status">{{ $t('intake.portal.needsLogin') }}</p>
    <p v-else-if="!loading && !session" class="rb-ob-status" role="status">{{ $t('intake.onlineSession.noSession') }}</p>

    <ul class="rb-session-meta" :aria-label="$t('intake.onlineSession.waitMetaAria')">
      <li>
        <span class="rb-session-meta-k">{{ $t('intake.onlineSession.waitTherapist') }}</span>
        <span class="rb-session-meta-v">{{ therapistName }}</span>
      </li>
      <li>
        <span class="rb-session-meta-k">{{ $t('intake.onlineSession.waitTime') }}</span>
        <span class="rb-session-meta-v">{{ sessionTime }}</span>
      </li>
      <li>
        <span class="rb-session-meta-k">{{ $t('intake.onlineSession.waitStart') }}</span>
        <span class="rb-session-meta-v">{{ startLabel }}</span>
      </li>
    </ul>

    <div class="rb-ob-actions">
      <button class="rb-ob-submit" type="button" :disabled="!session || refreshing" @click="checkStart">
        {{ canEnter ? $t('intake.onlineSession.waitEnterRoom') : $t('intake.onlineSession.waitCheckStart') }}
      </button>
      <button
          v-if="joinUrl && canEnter"
          class="rb-ob-submit rb-ob-submit-secondary"
          type="button"
          @click="openExternal"
      >
        {{ $t('intake.onlineSession.openJoinLink') }}
      </button>
      <button class="rb-ob-submit rb-ob-submit-secondary" type="button" @click="leave">
        {{ $t('intake.onlineSession.waitLeave') }}
      </button>
      <button class="rb-ob-submit rb-ob-submit-secondary" type="button" @click="support">
        {{ $t('intake.onlineSession.waitSupport') }}
      </button>
      <p class="rb-ob-status" role="status" aria-live="polite">{{ formStatus }}</p>
      <div class="rb-ob-trust">
        <p>{{ $t('intake.trustPath') }}</p>
        <p>{{ $t('authUi.familyNote') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import {clientError} from '@/intake/clientMessages'
import {legacySessionSupportRoute} from '@/intake/sessionPreparation'
import {
  countdownToSession,
  doctorDisplayName,
  formatSessionListTitle,
  hasAuthToken,
  refreshJoinableSession,
  resolveJoinableSession,
  sessionJoinUrl,
  toFaDigits,
  requestSessionAdmission,
  admissionJoinUrl,
  createActiveCareSupportCase,
} from '@/intake/portalApi'

export default {
  name: 'SessionWaitStep',
  data() {
    return {
      loading: true,
      refreshing: false,
      needsLogin: false,
      session: null,
      formStatus: '',
      countdown: null,
      pollTimer: null,
      tickTimer: null,
    }
  },
  computed: {
    lang() {
      return this.$route.params.lang || 'fa'
    },
    therapistName() {
      return doctorDisplayName(this.session && this.session.doctor, this.lang) || this.$t('intake.onlineSession.waitTherapistValue')
    },
    sessionTime() {
      try {
        return formatSessionListTitle(this.session, this.lang) || this.$t('intake.onlineSession.waitTimeValue')
      } catch (e) {
        return this.$t('intake.onlineSession.waitTimeValue')
      }
    },
    canEnter() {
      return !!(this.session && this.session.can_enter)
    },
    joinUrl() {
      return sessionJoinUrl(this.session)
    },
    startLabel() {
      if (this.canEnter) return this.$t('intake.onlineSession.waitStartNow')
      if (this.countdown && this.countdown.ms >= 86400000) {
        const minutes = Math.floor(this.countdown.ms / 60000)
        const label = this.$t('intake.onlineSession.waitStartDays', {
          days: Math.floor(minutes / 1440),
          hours: Math.floor((minutes % 1440) / 60),
          minutes: minutes % 60,
        })
        return this.lang === 'fa' ? toFaDigits(label) : label
      }
      if (this.countdown && this.countdown.label) {
        return this.lang === 'fa' ? toFaDigits(this.countdown.label) : this.countdown.label
      }
      return this.$t('intake.onlineSession.waitStartValue')
    },
  },
  mounted() {
    this.bootstrap()
  },
  beforeDestroy() {
    this.clearTimers()
  },
  methods: {
    clearTimers() {
      if (this.pollTimer) clearInterval(this.pollTimer)
      if (this.tickTimer) clearInterval(this.tickTimer)
      this.pollTimer = null
      this.tickTimer = null
    },
    preferredId() {
      return this.$route.query.id || this.$route.params.sessionId
    },
    bootstrap() {
      this.loading = true
      this.needsLogin = !hasAuthToken()
      resolveJoinableSession(this.preferredId())
          .then((session) => {
            this.session = session
            try {
              this.countdown = countdownToSession(session)
            } catch (e) {
              this.countdown = null
            }
            this.startPolling()
          })
          .catch((e) => {
            this.formStatus = clientError(e, this.lang)
          })
          .finally(() => {
            this.loading = false
          })
    },
    startPolling() {
      this.clearTimers()
      this.tickTimer = setInterval(() => {
        try {
          this.countdown = countdownToSession(this.session)
        } catch (e) {
          this.countdown = null
        }
      }, 1000)
      this.pollTimer = setInterval(() => {
        this.refresh()
      }, 20000)
    },
    refresh() {
      if (!hasAuthToken() || this.refreshing) return Promise.resolve()
      this.refreshing = true
      this.formStatus = ''
      return refreshJoinableSession(this.preferredId() || (this.session && this.session.id))
          .then((session) => {
            if (session) {
              this.session = session
              try {
                this.countdown = countdownToSession(session)
              } catch (e) {
                this.countdown = null
              }
              if (session.can_enter) {
                this.formStatus = this.$t('intake.onlineSession.waitReadyStatus')
              }
            }
          })
          .catch((e) => {
            this.formStatus = clientError(e, this.lang)
          })
          .finally(() => {
            this.refreshing = false
          })
    },
    checkStart() {
      this.refresh().then(() => {
        if (this.canEnter) {
          this.$router.push({
            name: 'intakeSessionRoom',
            params: {lang: this.lang},
            query: this.session && this.session.id ? {id: String(this.session.id)} : {},
          })
          return
        }
        if (!this.formStatus) {
          this.formStatus = this.$t('intake.onlineSession.waitNotReady')
        }
      }).catch((e) => {
        this.formStatus = clientError(e, this.lang)
      })
    },
    openExternal() {
      if (!this.session || !this.session.id) return
      requestSessionAdmission(this.session.id).then((admission) => {
        const url = admissionJoinUrl(admission)
        if (!url) throw new Error('SESSION_JOIN_CREDENTIAL_UNAVAILABLE')
        window.open(url, '_blank', 'noopener')
      }).catch((e) => { this.formStatus = clientError(e, this.lang) })
    },
    leave() {
      this.$router.push({name: 'portalHome', params: {lang: this.lang}})
    },
    support() {
      const route = legacySessionSupportRoute(this.session, this.lang, 'wait')
      if (route) {
        this.formStatus = ''
        return this.$router.push(route)
      }
      return createActiveCareSupportCase('ACCESS', 'Client requested help while waiting for a session.').then(() => {
        this.formStatus = this.$t('intake.onlineSession.supportStatus')
      }).catch((e) => { this.formStatus = clientError(e, this.lang) })
    },
  },
}
</script>
